import { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Wand2, Send, Plus, Loader2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AutoTextarea } from "@/components/AutoTextarea";
import { EntityPick } from "@/components/studio/widgets/Pickers";
import { api, errMsg } from "@/lib/api";
import { useEntities, useAllEvents } from "@/lib/queries";
import { catColor } from "@/lib/lore";

const GROUPS = [["characters", "Featured characters", (c) => c === "Character", "characters"], ["locations", "Locations", (c) => c === "Location", "locations"], ["lore", "Other featured lore", (c) => c !== "Character" && c !== "Location" && c !== "Relationship", "lore"]];
const groupOf = (cat) => GROUPS.find(([, , f]) => f(cat))?.[0];
const Label = ({ children }) => <div className="mb-1.5 text-[10px] uppercase tracking-[0.16em] text-[#A8A090]">{children}</div>;

const Chips = ({ items, onRemove, testid }) => (
  <div className="mb-1.5 flex flex-wrap gap-1.5" data-testid={testid}>
    {items.map((e) => (
      <span key={e.id} className="flex items-center gap-1 rounded-full border border-[#2D3442] bg-[#13171F] px-2 py-0.5 text-xs text-[#F3EBDD]" data-testid={`${testid}-chip-${e.id}`}>
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: catColor(e.category) }} />{e.name}
        {onRemove && <button onClick={() => onRemove(e.id)} className="text-[#A8A090] hover:text-[#F3EBDD]" aria-label={`Remove ${e.name}`} data-testid={`${testid}-remove-${e.id}`}><X className="h-3 w-3" /></button>}
      </span>
    ))}
  </div>
);

const PickList = ({ k, label, pool, ids, set, editable, noun = "entries" }) => {
  const chosen = ids.map((id) => pool.find((e) => e.id === id)).filter(Boolean);
  return (
    <div data-testid={`info-${k}`}>
      <Label>{label}</Label>
      <Chips items={chosen} onRemove={editable && ((id) => set(ids.filter((x) => x !== id)))} testid={`info-${k}-list`} />
      {editable && <EntityPick entities={pool.filter((e) => !ids.includes(e.id))} value={null} onChange={(id) => set([...ids, id])} placeholder="Add…" search={`Search ${noun}…`} empty={`No ${noun} found.`} testid={`info-${k}-pick`} />}
      {!editable && !chosen.length && <p className="text-xs text-[#A8A090]">None yet.</p>}
    </div>
  );
};

const When = ({ info, set, editable }) => {
  const num = (v) => (v === "" || v === "-" ? null : Number.isInteger(Number(v)) ? Number(v) : undefined);
  const field = (key, ph) => <Input type="number" disabled={!editable} value={info[key] ?? ""} placeholder={ph} onChange={(e) => { const v = num(e.target.value); if (v !== undefined) set({ [key]: v }); }} className="h-8 w-24 text-xs" data-testid={`info-${key.replace("_", "-")}`} />;
  return (
    <div>
      <Label>When it takes place</Label>
      <div className="flex flex-wrap items-center gap-2 text-xs text-[#A8A090]">Year {field("year_from", "from")}<span>to</span>{field("year_to", "to")}</div>
      {info.year_from != null && info.year_to != null && info.year_to < info.year_from && <p className="mt-1 text-xs text-[#F87171]" data-testid="info-year-error">The end year can't be before the start year. It won't save until this is fixed.</p>}
      <Input disabled={!editable} value={info.era || ""} maxLength={120} placeholder="Era or season (optional), e.g. The Long Winter" onChange={(e) => set({ era: e.target.value })} className="mt-2 h-8 text-xs" data-testid="info-era" />
    </div>
  );
};

const Suggest = ({ cid, info, pool, set }) => {
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setFound((await api.get(`/items/${cid}/info/suggest`)).data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const fresh = (found || []).filter((e) => { const g = groupOf(e.category); return g && !(info[g] || []).includes(e.id); });
  const add = (e) => { const g = groupOf(e.category); set({ [g]: [...(info[g] || []), e.id] }); };
  return (
    <div className="rounded-md border border-dashed border-[#2D3442] p-2.5" data-testid="info-suggest">
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={run} disabled={busy || !pool.length} data-testid="info-suggest-button">{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1 h-3.5 w-3.5" />}Find lore named in this chapter</Button>
      {found && !fresh.length && <p className="mt-2 text-xs text-[#A8A090]" data-testid="info-suggest-empty">Everything named in the text is already listed.</p>}
      {fresh.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">
        {fresh.map((e) => <button key={e.id} onClick={() => add(e)} className="flex items-center gap-1 rounded-full border border-[#D97706]/50 px-2 py-0.5 text-xs text-[#F3EBDD] hover:bg-[#D97706]/15" data-testid={`info-suggest-add-${e.id}`}><Plus className="h-3 w-3 text-[#F59E0B]" />{e.name}</button>)}
        <button onClick={() => set(Object.fromEntries(GROUPS.map(([g]) => [g, [...new Set([...(info[g] || []), ...fresh.filter((e) => groupOf(e.category) === g).map((e) => e.id)])]])))} className="text-xs text-[#F59E0B] underline" data-testid="info-suggest-add-all">Add all</button>
      </div>}
    </div>
  );
};

const Ideas = ({ ideas, set, editable, onSend }) => {
  const [text, setText] = useState("");
  const add = () => { if (!text.trim()) return; set([...ideas, { id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, text: text.trim().slice(0, 1000), sent: false }]); setText(""); };
  return (
    <div data-testid="info-ideas">
      <Label>Ideas for the Lore Bible</Label>
      <ul className="mb-2 space-y-1.5">
        {ideas.map((i) => (
          <li key={i.id} className="flex items-start gap-2 rounded border border-[#2D3442] bg-[#13171F] px-2 py-1.5 text-xs text-[#F3EBDD]" data-testid={`info-idea-${i.id}`}>
            <span className="min-w-0 flex-1 whitespace-pre-wrap [overflow-wrap:anywhere]">{i.text}</span>
            {i.sent ? <span className="flex shrink-0 items-center gap-0.5 text-[#A8A090]" data-testid={`info-idea-sent-${i.id}`}><Check className="h-3 w-3" />Sent</span>
              : editable && <button onClick={() => onSend(i)} className="flex shrink-0 items-center gap-0.5 text-[#F59E0B] hover:underline" data-testid={`info-idea-send-${i.id}`}><Send className="h-3 w-3" />Send to Notes</button>}
            {editable && <button onClick={() => set(ideas.filter((x) => x.id !== i.id))} className="shrink-0 text-[#A8A090] hover:text-[#F3EBDD]" aria-label="Delete idea" data-testid={`info-idea-delete-${i.id}`}><X className="h-3.5 w-3.5" /></button>}
          </li>
        ))}
      </ul>
      {editable && <div className="flex gap-1.5">
        <Input value={text} maxLength={1000} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="A new place, a rule of magic, a rumour…" className="h-8 text-xs" data-testid="info-idea-input" />
        <Button size="sm" className="h-8" onClick={add} disabled={!text.trim()} data-testid="info-idea-add"><Plus className="h-3.5 w-3.5" /></Button>
      </div>}
    </div>
  );
};

export const ChapterInfo = ({ uid, cid, info, onChange, editable, onSendIdea }) => {
  const { data: ents = [] } = useEntities(uid);
  const { data: events = [] } = useAllEvents(uid);
  const evPool = useMemo(() => events.map((e) => ({ id: e.id, name: `${e.title}${e.display_date ? ` · ${e.display_date}` : ""}`, category: "Historical_Event" })), [events]);
  const pools = useMemo(() => Object.fromEntries(GROUPS.map(([k, , f]) => [k, ents.filter((e) => f(e.category))])), [ents]);
  const send = (idea) => { onChange({ ideas: (info.ideas || []).map((x) => (x.id === idea.id ? { ...x, sent: true } : x)) }); onSendIdea(idea.text); };
  return (
    <div className="space-y-5" data-testid="chapter-info">
      <When info={info} set={onChange} editable={editable} />
      {GROUPS.map(([k, label, , noun]) => <PickList key={k} k={k} label={label} noun={noun} pool={pools[k]} ids={info[k] || []} set={(v) => onChange({ [k]: v })} editable={editable} />)}
      {editable && <Suggest cid={cid} info={info} pool={ents} set={onChange} />}
      <div>
        <Label>Event summary</Label>
        <AutoTextarea disabled={!editable} value={info.summary || ""} maxLength={4000} maxVh={30} onChange={(e) => onChange({ summary: e.target.value })} placeholder="What happens in this chapter?" className="min-h-[80px] text-sm" data-testid="info-summary" />
      </div>
      <PickList k="event_ids" label="Timeline events" noun="events" pool={evPool} ids={info.event_ids || []} set={(v) => onChange({ event_ids: v })} editable={editable} />
      <Ideas ideas={info.ideas || []} set={(v) => onChange({ ideas: v })} editable={editable} onSend={send} />
      <p className="text-[11px] text-[#A8A090]" data-testid="info-footnote">The free lore check uses this to check dates, events, places and who appears.</p>
    </div>
  );
};
